/**
 * Shared markdown rendering primitives for the Upright chat surface.
 *
 * Two consumers, one source of truth for plugins / urlTransform /
 * components map:
 *
 *   1. `MarkdownText` — the assistant-ui `Text` component used inside
 *      `<Thread>` for streamed assistant messages. Built with
 *      `makeMarkdownText` from `@assistant-ui/react-ui`.
 *
 *   2. `ChatMarkdown` — a plain `<ReactMarkdown>` wrapper that renders
 *      a markdown string using the same configuration. Used by the
 *      `emit_callout` tool UI (PLT-4590) to render the callout's
 *      body markdown so inline `<badge>` / `<cite id="…"/>` directives
 *      and platform deep-links keep working inside callouts the way
 *      they do in the main message body.
 *
 * Both consumers MUST stay in lock-step: a plugin added on the main
 * surface should also fire inside callouts, otherwise an inline
 * citation in a callout body would render as raw text. Keeping the
 * config in one place is the only way to make that invariant
 * obvious to a future editor.
 */
import type { ReactNode } from 'react';
import { makeMarkdownText } from '@assistant-ui/react-ui';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { getCitationIdFromHref, makeCitationAwareUrlTransform } from './citation-markers';
import { getGeneratedImage, makeGeneratedImageAwareUrlTransform } from './generated-image-registry';
import { isLockBadgeHref, makeLockAwareUrlTransform } from './lock-badge-markers';
import { getToneBadgeFromHref, makeToneBadgeAwareUrlTransform } from './tone-badge-markers';
import { remarkCitationPills } from './remark-citation-pills';
import { remarkInlineEmphasis } from './remark-inline-emphasis';
import { remarkLockBadges } from './remark-lock-badges';
import { remarkStripLeakedMarkupTags } from './remark-strip-leaked-markup-tags';
import { remarkToneBadges } from './remark-tone-badges';
import { remarkTableSignColor } from './remark-table-sign-color';
import { remarkTableTitle } from './remark-table-title';
import { useIsTeam } from '../common/generic/hooks';
import { ChatTable } from './chat-table';
import { Badge } from './components';
import { CitationPillById, InlineLockBadge, PlatformLinkPill } from './upright-chat-tool-uis';
import { isUprightPlatformLink } from './upright-platform-link';
import { hideIncompleteTrailingLink } from './streaming-link-guard';
import { stripLeakedToolTags } from './leaked-tool-tag-guard';

/**
 * Preprocess applied to the markdown buffer before render, for both
 * `MarkdownText` (streaming) and `ChatMarkdown`. Strip leaked rendering-tool
 * tag emissions first (PLT-4968) so a `<headline_kpis>{…}</headline_kpis>`
 * block never reaches the renderer as raw JSON, then hide an incomplete
 * trailing link (PLT-4595).
 */
function preprocessChatMarkdown(s: string): string {
  return hideIncompleteTrailingLink(stripLeakedToolTags(s));
}

/**
 * Plugin order (for both `MarkdownText` and `ChatMarkdown`).
 *
 * Order is load-bearing for one pair:
 *   1. `remarkTableSignColor` MUST run before `remarkToneBadges` —
 *      the table plugin emits `<badge tone="…">…</badge>` directives
 *      into Direction-column cells that the badge plugin then turns
 *      into link nodes. Reverse the order and the directives render
 *      as plain text.
 *
 * `remarkTableTitle` (PLT-4835) hoists a bold "title paragraph"
 * immediately above a table into a `data-table-title` attribute on the
 * table itself, which `ChatTable` then renders in a dedicated title
 * row alongside copy + download icon buttons. The plugin only touches
 * tables and their preceding paragraph, so it's order-independent of
 * the other table / inline plugins.
 *
 * Citation, tone-badge, lock-badge, and inline-emphasis plugins walk
 * text nodes independently (different URL schemes / different tag
 * names) so their relative order beyond rule (1) is don't-care.
 *
 * Note (PLT-4590): callouts and follow-ups are no longer in this
 * pipeline. Callouts render through the `emit_callout` tool UI with
 * their bodies passed back through `ChatMarkdown` so the inline
 * directive set still works inside. Follow-ups are entirely a tool
 * call (`suggest_followups`) — there is no inline channel any more.
 *
 * `remarkInlineEmphasis` (PLT-4806) converts inline `<b>` / `<strong>`
 * / `<i>` / `<em>` tags to real mdast `strong` / `emphasis` nodes so
 * HTML-style emphasis can be used alongside the rest of the chat's
 * HTML-like inline directive set (`<badge>`, `<cite>`, `<private />`).
 * Markdown `**bold**` / `*italic*` keep working — they're already
 * turned into `strong` / `emphasis` by `remarkParse` upstream.
 */
const REMARK_PLUGINS = [
  remarkGfm,
  remarkTableTitle,
  remarkTableSignColor,
  remarkToneBadges,
  remarkCitationPills,
  remarkLockBadges,
  remarkInlineEmphasis,
];

/**
 * Non-team plugin list (PLT-5092). Identical to `REMARK_PLUGINS` plus
 * `remarkStripLeakedMarkupTags` appended LAST. By the time it runs, every
 * supported directive (`<badge>`, `<cite>`, `<private />`, `<b>`/`<strong>`/
 * `<i>`/`<em>`) has been converted to a non-`html` node by the plugins above,
 * so any leftover bare-tag `html` node is an unhandled leak (a `<bold>` typo,
 * a stray wrapper, a malformed unclosed `<badge …>`). The stripper drops those
 * nodes, keeping their inner text — so `<bold>Lorem</bold>` renders as `Lorem`.
 *
 * Team users render with `REMARK_PLUGINS` (no stripper) so leaked tags stay
 * visible for debugging. The team / non-team choice is made by the caller via
 * `useIsTeam()` — see `MarkdownText` / `MarkdownTextNonTeam` below and the
 * `Text` component selection in `upright-chat.tsx`.
 */
const REMARK_PLUGINS_NON_TEAM = [...REMARK_PLUGINS, remarkStripLeakedMarkupTags];

/**
 * `react-markdown`'s `defaultUrlTransform` strips any URL whose scheme
 * isn't on its safe-list (http, https, mailto, tel, …). The passthrough
 * helpers compose innermost-first: lock → badge → citation → default.
 * Any URL not matching one of the special schemes bubbles down to the
 * default sanitizer so external links stay safe.
 */
export const chatUrlTransform = makeLockAwareUrlTransform(
  makeToneBadgeAwareUrlTransform(
    makeCitationAwareUrlTransform(makeGeneratedImageAwareUrlTransform(defaultUrlTransform)),
  ),
);

/**
 * `components` overrides shared between `MarkdownText` and
 * `ChatMarkdown`. The `a` override dispatches by URL scheme to the
 * matching pill / chip / badge. Unknown links fall through to a
 * regular anchor.
 */
function ChatLink({ href, children, className, ...rest }: { href?: string; children?: ReactNode; className?: string }) {
  const citationId = getCitationIdFromHref(href);
  if (citationId !== null) return <CitationPillById id={citationId} />;
  const badge = getToneBadgeFromHref(href);
  if (badge !== null) return <Badge tone={badge.tone}>{badge.label}</Badge>;
  if (isLockBadgeHref(href)) return <InlineLockBadge />;
  if (typeof href === 'string' && isUprightPlatformLink(href)) {
    return <PlatformLinkPill href={href}>{children}</PlatformLinkPill>;
  }
  // PLT-4580 follow-up — every other external link in chat prose (typical
  // case: `[Title](https://pmc.ncbi.nlm.nih.gov/...)` for a scientific
  // article, or any other model-emitted external URL) gets the same lilac
  // tint that we already use for platform deep-links and follow-up chips.
  // Without `chat-external-link`, `aui-md-a` falls back to assistant-ui's
  // blue, which clashes with the rest of the chat link aesthetic and
  // makes article links read as "regular blue links" instead of matching
  // the suggestion-chip / platform-link palette. Open in a new tab for
  // safety — these are off-platform navigations.
  const isExternal = typeof href === 'string' && /^https?:\/\//i.test(href);
  const cn = ['aui-md-a', isExternal ? 'chat-external-link' : null, className].filter(Boolean).join(' ');
  const externalAttrs = isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <a href={href} className={cn} {...externalAttrs} {...rest}>
      {children}
    </a>
  );
}

/**
 * `img` override (PLT-5148). The system prompt tells the model that a
 * `code_execution`-generated chart/plot "is displayed directly in the chat", so
 * the model writes a markdown image in its prose — `![chart](chart.png)` — with
 * a BARE FILENAME as the `src`. The sandbox file itself was downloaded
 * server-side and delivered over the `tool-result-files` SSE event; the adapter
 * indexed it by filename in the generated-image registry. We resolve the `src`
 * against that registry and swap in the base64 data URL.
 *
 * Resolution outcomes:
 *   - Registered generated image → render it inline (full chat width).
 *   - A real http(s) URL the model emitted (rare, but a legitimately reachable
 *     image) → render it as-is.
 *   - Anything else (a bare filename whose bytes haven't arrived yet, or never
 *     will) → render the alt text as a muted caption, NOT a broken-image icon.
 *     During streaming the prose `![…]` reference can momentarily precede the
 *     `tool-result-files` event; this fallback avoids a flash of broken image,
 *     and once the event lands the registry resolves on the next render pass.
 */
function ChatImage({ src, alt }: { src?: string; alt?: string }) {
  const generated = getGeneratedImage(src);
  if (generated) {
    return (
      <img
        src={generated.dataUrl}
        alt={alt || generated.filename || 'Generated image'}
        className="chat-inline-image"
        style={{ display: 'block', maxWidth: '100%', height: 'auto', borderRadius: '4px', margin: '8px 0' }}
      />
    );
  }
  if (typeof src === 'string' && /^https?:\/\//i.test(src)) {
    return (
      <img
        src={src}
        alt={alt || 'Image'}
        className="chat-inline-image"
        style={{ display: 'block', maxWidth: '100%', height: 'auto', borderRadius: '4px', margin: '8px 0' }}
      />
    );
  }
  // Unresolved bare-filename reference — show the alt text (the model's
  // caption) rather than a broken-image icon. Empty alt renders nothing.
  return alt ? (
    <span className="chat-inline-image-fallback" style={{ opacity: 0.6, fontStyle: 'italic' }}>
      {alt}
    </span>
  ) : null;
}

const CHAT_MARKDOWN_COMPONENTS = {
  a: ChatLink,
  table: ChatTable,
  img: ChatImage,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

/**
 * Assistant-ui `Text` component. Rendered inside `<Thread>` for
 * streamed assistant messages. Configuration mirrors
 * `ChatMarkdown` exactly — see `REMARK_PLUGINS` and
 * `CHAT_MARKDOWN_COMPONENTS` above.
 *
 * `preprocess` is forwarded to `MarkdownTextPrimitive`, which applies
 * it to the streaming buffer before each render. See
 * `hideIncompleteTrailingLink` (in `streaming-link-guard.ts`) for why
 * it's needed.
 */
export const MarkdownText = makeMarkdownText({
  remarkPlugins: REMARK_PLUGINS,
  urlTransform: chatUrlTransform,
  components: CHAT_MARKDOWN_COMPONENTS,
  preprocess: preprocessChatMarkdown,
});

/**
 * Non-team variant of `MarkdownText` (PLT-5092) — identical config except it
 * runs `REMARK_PLUGINS_NON_TEAM`, which strips leftover leaked markup tags.
 * `makeMarkdownText` bakes the plugin list in at creation time and the
 * `preprocess`/plugins can't read React context, so the team / non-team choice
 * is made one level up by swapping the whole `Text` component — see the
 * `useIsTeam()` selection in `upright-chat.tsx`.
 */
export const MarkdownTextNonTeam = makeMarkdownText({
  remarkPlugins: REMARK_PLUGINS_NON_TEAM,
  urlTransform: chatUrlTransform,
  components: CHAT_MARKDOWN_COMPONENTS,
  preprocess: preprocessChatMarkdown,
});

/**
 * Plain react-markdown wrapper used by the `emit_callout` tool UI
 * (PLT-4590) to render the callout's body markdown with the same
 * plugin/components set as the main message body. Inline `<badge>` /
 * `<cite id="…"/>` directives, platform deep-links, GFM tables, and
 * follow-up chips therefore work the same way inside a callout as
 * they do outside it — which is what the system prompt promises.
 *
 * The same `hideIncompleteTrailingLink` guard is applied here for
 * consistency with `MarkdownText`. Callout bodies arrive as a single
 * tool argument so mid-link rendering is unlikely, but the helper is
 * a no-op for complete links and keeps both surfaces behaviour-
 * identical.
 */
export function ChatMarkdown({ children }: { children: string }) {
  // PLT-5092: non-team callers also get leaked markup tags stripped inside
  // callout bodies, matching the main message body. `ChatMarkdown` is a React
  // component (unlike the static `makeMarkdownText` output), so it can read the
  // team flag directly via the hook and pick the plugin list inline.
  const isTeam = useIsTeam();
  const remarkPlugins = isTeam ? REMARK_PLUGINS : REMARK_PLUGINS_NON_TEAM;
  return (
    <ReactMarkdown remarkPlugins={remarkPlugins} urlTransform={chatUrlTransform} components={CHAT_MARKDOWN_COMPONENTS}>
      {preprocessChatMarkdown(children)}
    </ReactMarkdown>
  );
}
